import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createDirectoryWriter,
  runDownload,
  type DownloadFailure,
  type DownloadFileTransport,
  type DownloadProgress,
} from "@/components/dataset/download";
import { createLogger } from "@/logging";
import {
  annotationSetDownloadErrorMessage,
  assertNoAnnotationFolder,
  collectAnnotationSetFiles,
  createAnnotationSetTransport,
  toAnnotationSetDownloadEntries,
  type AnnotationSetDownloadEntry,
} from "./annotationSetDownload";

const logger = createLogger("page/annotationStudio/download");

export type AnnotationSetDownloadPhase = "idle" | "preparing" | "downloading" | "done" | "error";

export type AnnotationSetDownloadState = {
  phase: AnnotationSetDownloadPhase;
  // 받는 중이거나 마지막으로 받은 세트의 표시 이름.
  setLabel: string | null;
  progress: DownloadProgress;
  failures: DownloadFailure<AnnotationSetDownloadEntry>[];
  cancelled: boolean;
  // 시작 전(폴더 확인·목록 조회) 실패 사유.
  errorMessage: string | null;
};

// 받을 세트다. 표시 이름은 호출부가 세트 목록과 같은 규칙으로 만든다.
export type AnnotationSetDownloadTarget = {
  uuid: string;
  label: string;
};

export type AnnotationSetDownloadController = AnnotationSetDownloadState & {
  active: boolean;
  /** 고른 폴더에 세트 파일을 받는다. 폴더는 클릭 핸들러에서 미리 골라 넘긴다. */
  start: (target: AnnotationSetDownloadTarget, directory: FileSystemDirectoryHandle) => void;
  cancel: () => void;
  retryFailed: () => void;
  dismiss: () => void;
};

const IDLE: AnnotationSetDownloadState = {
  phase: "idle",
  setLabel: null,
  progress: { total: 0, completed: 0, skipped: 0, failed: 0 },
  failures: [],
  cancelled: false,
  errorMessage: null,
};

type RunContext = {
  setLabel: string;
  directory: FileSystemDirectoryHandle;
  transport: DownloadFileTransport<AnnotationSetDownloadEntry>;
};

/**
 * Annotation Set 파일을 내려받는다. 흐름은 폴더 확인 -> 목록 조회 -> 러너 실행이다.
 *
 * Studio binding 이 소유한다. 세트 패널은 GPU 잠금·세트 종류에 따라 다시 마운트된다. 그래서
 * 진행 상태를 패널에 두면 받는 도중 사라진다.
 *
 * 실패분 재시도는 같은 폴더·transport 로 실패한 항목만 다시 받는다. 목록을 다시 조회하지 않는다.
 * 폴더도 다시 확인하지 않는다. 그 폴더는 이번 다운로드가 만들었기 때문이다.
 */
export function useAnnotationSetDownload(): AnnotationSetDownloadController {
  const [state, setState] = useState<AnnotationSetDownloadState>(IDLE);
  const abortRef = useRef<AbortController | null>(null);
  const contextRef = useRef<RunContext | null>(null);

  const execute = useCallback(
    async (
      context: RunContext,
      entries: readonly AnnotationSetDownloadEntry[],
      controller: AbortController,
    ) => {
      // 새 실행이 이 실행을 대신했으면 늦게 온 진행·완료가 새 상태를 덮지 않게 버린다.
      const isCurrent = () => abortRef.current === controller;
      setState({
        ...IDLE,
        phase: "downloading",
        setLabel: context.setLabel,
        progress: { total: entries.length, completed: 0, skipped: 0, failed: 0 },
      });
      const outcome = await runDownload(
        context.transport,
        createDirectoryWriter(context.directory),
        entries,
        {
          signal: controller.signal,
          onProgress: (progress) => {
            if (isCurrent()) setState((current) => ({ ...current, progress }));
          },
        },
      );
      if (!isCurrent()) return;
      setState({
        phase: "done",
        setLabel: context.setLabel,
        progress: {
          total: outcome.total,
          completed: outcome.completed,
          skipped: outcome.skipped,
          failed: outcome.failed,
        },
        failures: outcome.failures,
        cancelled: outcome.cancelled,
        errorMessage: null,
      });
      if (outcome.cancelled) return;
      if (outcome.failures.length > 0) {
        logger.error("annotation set download finished with failures", {
          failed: outcome.failures.length,
          total: outcome.total,
        });
        toast.error(
          `Annotation Set 파일 ${outcome.failures.length}개를 내려받지 못했습니다. 다시 시도해 주세요.`,
        );
        return;
      }
      toast.success(`Annotation Set 파일 ${outcome.total}개를 내려받았습니다.`);
    },
    [],
  );

  const start = useCallback(
    (target: AnnotationSetDownloadTarget, directory: FileSystemDirectoryHandle) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const setLabel = target.label;
      setState({ ...IDLE, phase: "preparing", setLabel });

      void (async () => {
        let entries: AnnotationSetDownloadEntry[];
        try {
          await assertNoAnnotationFolder(directory);
          entries = toAnnotationSetDownloadEntries(
            await collectAnnotationSetFiles(target.uuid, controller.signal),
          );
        } catch (error) {
          if (controller.signal.aborted) return;
          logger.error("annotation set download could not start", error);
          setState({
            ...IDLE,
            phase: "error",
            setLabel,
            errorMessage: annotationSetDownloadErrorMessage(error),
          });
          return;
        }
        const context = {
          setLabel,
          directory,
          transport: createAnnotationSetTransport(target.uuid),
        };
        contextRef.current = context;
        await execute(context, entries, controller);
      })();
    },
    [execute],
  );

  // Studio 가 사라지면(Dataset 상세 이탈) 중단한다. 진행 상태를 이어받을 곳이 없다.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setState((current) => ({ ...current, phase: "done", cancelled: true }));
  }, []);

  const retryFailed = useCallback(() => {
    const context = contextRef.current;
    if (context === null || state.failures.length === 0) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void execute(
      context,
      state.failures.map((failure) => failure.entry),
      controller,
    );
  }, [execute, state.failures]);

  const dismiss = useCallback(() => {
    abortRef.current?.abort();
    setState(IDLE);
  }, []);

  return {
    ...state,
    active: state.phase === "preparing" || state.phase === "downloading",
    start,
    cancel,
    retryFailed,
    dismiss,
  };
}
